/**
 * LinkAccount.jsx — Forced provider linking screen (June 2026 auth restriction).
 *
 * Shown to grandfathered email/password users after login. Blocks access to the
 * rest of the app until they link a Google, Phone, or Apple account.
 *
 * Signals to backend:
 *   - Google:  window redirect → /discover comes back with the linked provider
 *   - Apple:   AppleID.auth.signIn() popup → POST /auth/apple/session (merges providers)
 *   - Phone:   PhoneAuthPanel → /auth/phone/send-code + /auth/phone/link
 *
 * Once any one provider is linked, backend `must_link_provider` flips to false
 * on the next /auth/me refresh and ProtectedRoute stops bouncing here.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, api } from '@/contexts/AuthContext';
import PhoneAuthPanel from '@/components/PhoneAuthPanel';
import { Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { launchGoogleSignIn, getGoogleAuthConfig } from '@/lib/googleAuth';

const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || '';
const APPLE_ENABLED = Boolean(APPLE_SERVICES_ID);

export default function LinkAccount() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  // Users who arrive here but somehow don't need linking → send them onward.
  useEffect(() => {
    if (user && !user.must_link_provider) {
      navigate(user.profile_complete ? '/discover' : '/profile/setup', { replace: true });
    }
  }, [user, navigate]);

  const handleGoogle = () => {
    // Feb 2026 — direct-Google flow when the backend has GOOGLE_CLIENT_ID
    // + GOOGLE_CLIENT_SECRET configured; otherwise falls back to the
    // Emergent-hosted OAuth handoff. Client ID is fetched from
    // /api/auth/google/config at runtime (no REACT_APP_* build-time
    // dependency). Backend merges providers by email match either way.
    launchGoogleSignIn();
  };

  // Warm the config cache so the first tap doesn't stall on a fetch.
  useEffect(() => { getGoogleAuthConfig().catch(() => {}); }, []);

  const loadAppleSdk = () =>
    new Promise((resolve, reject) => {
      if (window.AppleID) return resolve(window.AppleID);
      const existing = document.getElementById('apple-signin-sdk');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.AppleID));
        existing.addEventListener('error', reject);
        return;
      }
      const s = document.createElement('script');
      s.id = 'apple-signin-sdk';
      s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      s.async = true;
      s.onload = () => resolve(window.AppleID);
      s.onerror = reject;
      document.head.appendChild(s);
    });

  const handleApple = async () => {
    if (!APPLE_ENABLED) { toast.error('Apple Sign-In not configured'); return; }
    setLoading(true);
    try {
      await loadAppleSdk();
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: 'name email',
        redirectURI: window.location.origin + '/auth/apple/callback',
        state: 'link',
        usePopup: true,
      });
      const data = await window.AppleID.auth.signIn();
      const payload = {
        identity_token: data.authorization?.id_token || '',
        authorization_code: data.authorization?.code,
      };
      if (data.user?.name) {
        payload.full_name = {
          given_name: data.user.name.firstName,
          family_name: data.user.name.lastName,
        };
      }
      await api.post('/auth/apple/session', payload);
      await checkAuth();
      toast.success('Apple account linked');
      navigate(user?.profile_complete ? '/discover' : '/profile/setup', { replace: true });
    } catch (err) {
      if (err?.error === 'popup_closed_by_user') return;
      toast.error(err.response?.data?.detail || 'Could not link Apple account');
    } finally { setLoading(false); }
  };

  const handleSignOut = async () => {
    try { await logout(); } catch { /* best-effort */ }
    navigate('/auth', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="link-account-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#F4F5F8] flex items-center justify-center mb-4">
            <ShieldCheck size={28} className="text-[#0F0F10]" />
          </div>
          <h1 className="text-[26px] font-[800] tracking-[-0.6px] leading-tight text-[#0F0F10] mb-2">
            One more quick step
          </h1>
          <p className="text-[14px] text-[#6B7280] leading-snug max-w-[320px]">
            To keep your account secure, link a Google, Phone, or Apple sign-in to continue.
            You&rsquo;ll only need to do this once.
          </p>
        </div>

        {/* Google */}
        <button
          onClick={handleGoogle}
          data-testid="link-google-btn"
          disabled={loading}
          className="w-full rounded-xl bg-white border border-black/[0.08] hover:bg-[#FAFAFB] py-3.5 mb-3 text-sm font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-60"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Link Google account
        </button>

        {/* Apple */}
        {APPLE_ENABLED && (
          <button
            onClick={handleApple}
            data-testid="link-apple-btn"
            disabled={loading}
            className="w-full rounded-xl bg-[#0F0F10] text-white hover:bg-[#1F1F22] py-3.5 mb-3 text-sm font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <svg className="w-[18px] h-[18px] -mt-0.5" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                Link Apple account
              </>
            )}
          </button>
        )}

        {/* Phone */}
        {!showPhone ? (
          <button
            onClick={() => setShowPhone(true)}
            data-testid="link-phone-toggle"
            className="w-full rounded-xl bg-white border border-black/[0.08] hover:bg-[#FAFAFB] py-3.5 mb-3 text-sm font-semibold transition-all flex items-center justify-center gap-3"
          >
            Link phone number
          </button>
        ) : (
          <div className="mb-3 p-4 rounded-xl border border-black/[0.06] bg-white" data-testid="link-phone-panel">
            <PhoneAuthPanel
              referralCode=""
              onAuthSuccess={async () => {
                await checkAuth();
                toast.success('Phone number linked');
                navigate(user?.profile_complete ? '/discover' : '/profile/setup', { replace: true });
              }}
            />
            <button
              onClick={() => setShowPhone(false)}
              data-testid="link-phone-cancel"
              className="w-full mt-2 text-[12px] font-semibold text-zinc-500 hover:text-[#0F0F10]"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Sign out escape hatch */}
        <button
          onClick={handleSignOut}
          data-testid="link-signout-btn"
          className="w-full mt-6 text-[12px] font-semibold text-zinc-500 hover:text-[#0F0F10] transition-colors inline-flex items-center justify-center gap-1.5"
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </div>
  );
}
